import {type TranslationObject} from '@colonist/i18n'
import {type UIBaseShareImage} from '../ui_share_image/UIBaseShareImage.ts'
import {ViewPopup} from './ViewPopup.ts'
import {createExternalImgIn, createParagraphIn, setElementInnerHTML, setElementOnClick} from '../ui_utils/DomUtils.ts'
import {type AjaxDataTypes} from '../../../../shared/AjaxDataTypes.ts'
import {AnalyticEventCategory, AnalyticEventType, AnalyticEventValue, MobileImageSharingAnalytics} from '../../../../shared/entity_datas/EntityDatas.ts'
import {uiManagerEvents} from '../EntryShared.ts'
import {UIDeviceController} from '../ui_html/UIDeviceController.ts'
import {UIPopupController} from './UIPopupController.ts'
import {clientI18n} from '../ClientI18n.ts'
import {UIUrlParameterNames} from '../ui_html/UIUrlController.ts'
import {CA} from '../analytics/Analytics.ts'
import {UIEndGameTitleImage} from '../ui_share_image/UIEndGameTitleImage.ts'
import {type ImageURL} from '@colonist/types'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'

export class ViewPopupShareImage extends ViewPopup {
    private readonly currentImage: UIBaseShareImage
    private readonly otherImage?: UIBaseShareImage | undefined
    private readonly showingTitleImage: boolean
    private readonly onReplayShare?: (() => Promise<void>) | undefined
    private shareAction: (shareImage: UIBaseShareImage) => Promise<void>

    constructor(currentImage: UIBaseShareImage, otherImage?: UIBaseShareImage, showingTitleImage: boolean = false, title: TranslationObject = {key: 'strings:game.endGame.shareImagePopup.title'}, onReplayShare?: () => Promise<void>) {
        const body: TranslationObject = {key: 'strings:colonistCommon.utils.emptyString'}

        super(title, body, () => undefined, () => undefined, true)

        this.currentImage = currentImage
        this.otherImage = otherImage
        this.showingTitleImage = showingTitleImage
        this.onReplayShare = onReplayShare

        this.contentContainer.classList.add('popup-share-image')

        setElementInnerHTML(this.body, {key: 'strings:colonistCommon.utils.emptyString'})
        void this.loadImage()

        this.setupButtons()
    }

    private setupButtons() {
        if(UIUtilsEjsVariables.isUserOnDiscord) return this.setupDiscordButtons()
        if(UIUtilsEjsVariables.isUserOnMobile) return this.setupMobileButtons()
        this.setupDefaultButtons()
    }

    private setupDiscordButtons() {
        this.shareAction = async(image: UIBaseShareImage) => {
            const imageFile = await image.getImageFile()
            if(imageFile == undefined) return

            uiManagerEvents.shareImage(undefined, undefined, undefined, imageFile, undefined)

            ViewPopupShareImage.sendDiscordShareAnalytic()
        }

        this.changeCheckButtonText({key: 'strings:game.endGame.cornerButton.share'})
        setElementOnClick(this.checkButton, async() => {
            await this.shareAction(this.currentImage)
            this.sendShareTitleAnalytic()
        })
        this.removeXButton()
    }

    private static sendDiscordShareAnalytic() {
        const discordShareClickEvent: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.EndGameShareButton,
            category: AnalyticEventCategory.Virality,
            floatValue: AnalyticEventValue.EndGameShareDiscordButton,
        }
        void CA.sendAnalyticEventOnce(discordShareClickEvent)
    }

    private setupMobileButtons() {
        this.shareAction = async(image: UIBaseShareImage) => {
            if(UIDeviceController.isUserOnFirefox()) {
                UIPopupController.createSimplePopup({key: 'strings:popups.errors.sharingNotSupportedOnFirefox.title'}, {key: 'strings:popups.errors.sharingNotSupportedOnFirefox.message'})
                return
            }

            const title = clientI18n.t('strings:game.endGame.shareImagePopup.title')
            const text = clientI18n.t('strings:homePage.popups.discordShareActivity.text')
            const link = `${window.location.origin}/mobile#${UIUrlParameterNames.MobileImageSharing}=${MobileImageSharingAnalytics[MobileImageSharingAnalytics.GameResultsSharedFromGameEndScreen]}`
            const imageFile = await image.getImageFile() as File
            const imageSrc = await ViewPopupShareImage.fileToDataUrl(imageFile)
            if(imageSrc == undefined) return

            uiManagerEvents.shareImage(title, text, link, imageFile, imageSrc)

            ViewPopupShareImage.sendMobileShareAnalytic()
        }

        this.changeCheckButtonText({key: 'strings:game.endGame.shareImagePopup.mobile.shareImageButtonText'})
        setElementOnClick(this.checkButton, async() => {
            await this.shareAction(this.currentImage)
            this.sendShareTitleAnalytic()
        })

        if(this.onReplayShare == undefined) {
            this.removeXButton()
            return
        }

        const replayShare = this.onReplayShare
        this.changeXButtonText({key: 'strings:game.endGame.shareImagePopup.mobile.shareReplayButtonText'})
        setElementOnClick(this.xButton, async() => {
            await replayShare()
        })
    }

    private static fileToDataUrl(file: File): Promise<string | undefined> {
        return new Promise((resolve) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = () => resolve(undefined)
            reader.readAsDataURL(file)
        })
    }

    private static sendMobileShareAnalytic() {
        const mobileShareClickEvent: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.EndGameShareButton,
            category: AnalyticEventCategory.Virality,
            floatValue: AnalyticEventValue.EndGameShareMobileButton,
        }
        void CA.sendAnalyticEventOnce(mobileShareClickEvent)
    }

    private setupDefaultButtons() {
        this.changeCheckButtonText({key: 'strings:game.endGame.shareImagePopup.copy'})
        this.changeXButtonText({key: 'strings:game.endGame.shareImagePopup.download'})

        setElementOnClick(this.checkButton, async() => {
            await this.copyFileToClipboard()
            this.changeCheckButtonText({key: 'strings:game.endGame.shareImagePopup.copied'})
            setTimeout(() => {
                this.changeCheckButtonText({key: 'strings:game.endGame.shareImagePopup.copy'})
            }, 1500)
            void ViewPopupShareImage.sendCopyButtonClickAnalytic()
            this.sendShareTitleAnalytic()
        })

        setElementOnClick(this.xButton, () => {
            void this.downloadFile()
            this.removePopup()
            void ViewPopupShareImage.sendDownloadButtonClickAnalytic()
            this.sendShareTitleAnalytic()
        })
    }

    private async loadImage() {
        const imageFile = await this.currentImage.getImageFile()
        if(imageFile == undefined) return

        const url = URL.createObjectURL(imageFile) as ImageURL

        createExternalImgIn(this.body, url, {key: 'strings:game.endGame.shareImagePopup.title'})

        if(this.otherImage != undefined) this.createSwitchImageText()

        this.show()
    }

    private createSwitchImageText() {
        const text: TranslationObject = this.showingTitleImage ? {key: 'strings:game.endGame.shareImagePopup.viewResults'} : {key: 'strings:game.endGame.shareImagePopup.viewTitle'}

        const switchImageText = createParagraphIn(this.body, text, 'switch-image-text')
        setElementOnClick(switchImageText, () => this.displayOtherImage())
    }

    private displayOtherImage() {
        if(this.otherImage == undefined) return

        new ViewPopupShareImage(this.otherImage, this.currentImage, !this.showingTitleImage, undefined, this.onReplayShare)
        this.removePopup()
    }

    private async copyFileToClipboard() {
        const imageFile = await this.currentImage.getImageFile()
        if(imageFile == undefined) return

        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    [imageFile.type]: imageFile,
                }),
            ])
        } catch(error) {
            // eslint-disable-next-line no-console
            console.error('Error copying file to clipboard. Clipboard Permissions likely missing', error)
            this.changeCheckButtonText({key: 'strings:game.endGame.shareImagePopup.cantCopy'})
        }
    }

    private async downloadFile() {
        const imageFile = await this.currentImage.getImageFile()
        if(imageFile == undefined) return

        const url = URL.createObjectURL(imageFile)

        const a = document.createElement('a')
        a.href = url
        a.download = imageFile.name

        document.body.appendChild(a)

        a.click()

        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    private static sendCopyButtonClickAnalytic() {
        const copyButtonClickEvent: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.EndGameShareButton,
            category: AnalyticEventCategory.Virality,
            floatValue: AnalyticEventValue.EndGameShareCopyButton,
        }
        void CA.sendAnalyticEventOnce(copyButtonClickEvent)
    }

    private static sendDownloadButtonClickAnalytic() {
        const downloadButtonClickEvent: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.EndGameShareButton,
            category: AnalyticEventCategory.Virality,
            floatValue: AnalyticEventValue.EndGameShareDownloadButton,
        }
        void CA.sendAnalyticEventOnce(downloadButtonClickEvent)
    }

    private sendShareTitleAnalytic() {
        if(this.currentImage instanceof UIEndGameTitleImage == false) return
        const titleEnum = (this.currentImage as UIEndGameTitleImage).title

        const shareTitleEvent: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.EndGameShareTitle,
            category: AnalyticEventCategory.Virality,
            floatValue: titleEnum,
        }
        void CA.sendAnalyticEventOnce(shareTitleEvent)
    }
}
